import type { AxiosError } from 'axios';
import { type SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Button from '@/components/Common/Button';
import LayoutGap from '@/components/Common/LayoutGap';
import Form from '@/components/ui/Form';
import { Separator } from '@/components/ui/Separator';
import EmptyState from '@/components/Common/EmptyState';
import CloseIcon from 'assets/icons/x.svg?react';

import { useMemo } from 'react';
import { DisplayValue } from '@/components/Common/DisplayData';
import {
  type SegmentDetailsFormValues,
  segmentDetailsSchema,
  segmentTypeOptions,
  ruleTypeOptions,
  operatorOptions,
  listOperatorOptions,
  transactionRuleSchema,
  userRuleSchema,
  activeStatusOptions,
  inactiveStatusOptions,
  inProgressStatusOptions,
  draftStatusOptions,
} from '../schema';

type SegmentDetailsFormProps = {
  defaultValues?: SegmentDetailsFormValues;
  onSubmit: SubmitHandler<SegmentDetailsFormValues>;
  isSubmitting: boolean;
  submitError?: AxiosError | null;
  isEditing?: boolean;
};

const SegmentDetailsForm = ({
  defaultValues = segmentDetailsSchema.cast({}),
  onSubmit,
  isSubmitting,
  submitError,
  isEditing = false,
}: SegmentDetailsFormProps) => {
  const isReadOnly = useMemo(
    () => ['INACTIVE', 'IN_PROGRESS'].includes(defaultValues.status),
    [defaultValues.status],
  );

  const form = useForm<SegmentDetailsFormValues>({
    resolver: yupResolver(segmentDetailsSchema),
    defaultValues,
    mode: 'onTouched',
    disabled: isReadOnly,
  });

  const isActive = defaultValues.status === 'ACTIVE';

  const rules = useFieldArray({
    control: form.control,
    name: 'rules',
  });

  const changeRuleType = (index: number, value: string) => {
    if (value === 'purchases') {
      rules.update(index, transactionRuleSchema.cast({}));
    } else if (value === 'user') {
      rules.update(index, userRuleSchema.cast({}));
    }
  };

  /**
   * This is a workaround to check if the form is dirty because the formState.isDirty
   * is not working as expected and always returns true
   */
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  const segmentStatus = form.watch('status');

  const isSubmitDisabled = useMemo(() => {
    // Active segment cannot be edited except for changing the status to INACTIVE
    const isRemainingActive =
      defaultValues.status === 'ACTIVE' && segmentStatus === 'ACTIVE';

    return !isDirty || isRemainingActive || form.formState.disabled;
  }, [form.formState.disabled, defaultValues.status, segmentStatus, isDirty]);

  const statusOptions = useMemo(() => {
    if (defaultValues.status === 'ACTIVE') {
      return activeStatusOptions;
    }
    if (defaultValues.status === 'INACTIVE') {
      return inactiveStatusOptions;
    }
    if (defaultValues.status === 'IN_PROGRESS') {
      return inProgressStatusOptions;
    }
    return draftStatusOptions;
  }, [defaultValues.status]);

  const rulesArrayError = useMemo(
    () =>
      form.formState.errors.rules?.message ||
      form.formState.errors.rules?.root?.message,
    [form.formState.errors.rules],
  );

  const segmentType = form.watch('segmentType');
  const isMutable = segmentType === 'MUTABLE';

  return (
    <Form.Provider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <LayoutGap className="max-h-[70vh] items-stretch overflow-auto">
          <LayoutGap direction="column" className="sticky top-0 flex-grow">
            {isEditing && (
              <DisplayValue label="ID" type="id" value={defaultValues.id} />
            )}
            <Form.Input
              label="Name"
              name="name"
              type="text"
              autoComplete="off"
              placeholder="Enter segment name"
              disabled={isActive}
            />
            <Form.Select
              label="Status"
              name="status"
              placeholder="Select status"
              options={statusOptions}
              disabled={isReadOnly}
              description={
                <>
                  {defaultValues.status === 'DRAFT' && (
                    <>
                      Segment will be built when the status is changed to{' '}
                      <span className="font-bold">ACTIVE</span>.
                    </>
                  )}
                  {defaultValues.status === 'ACTIVE' && (
                    <>
                      Segment will stop adding new users when the status is
                      changed to <span className="font-bold">INACTIVE</span>.
                    </>
                  )}
                  {defaultValues.status === 'INACTIVE' && (
                    <>
                      Segment will stop adding new users when the status is
                      changed to <span className="font-bold">INACTIVE</span>.
                    </>
                  )}
                  {defaultValues.status === 'IN_PROGRESS' && (
                    <>Segment is being built.</>
                  )}
                </>
              }
            />
            <Form.Select
              name="segmentType"
              label="Type"
              placeholder="Select segment type"
              options={segmentTypeOptions}
              disabled={isActive}
              description={
                <>
                  {form.getValues('segmentType') === 'IMMUTABLE' && (
                    <>
                      <strong className="font-bold">IMMUTABLE</strong> segments
                      will not add new users after the initial build.
                    </>
                  )}
                  {form.getValues('segmentType') === 'MUTABLE' && (
                    <>
                      <strong className="font-bold">MUTABLE</strong> segments
                      will continue to add new users while{' '}
                      <strong className="font-bold">ACTIVE</strong>.
                    </>
                  )}
                </>
              }
            />
            {isEditing && (
              <>
                <DisplayValue
                  label="Size"
                  type="number"
                  value={defaultValues.size}
                />
                <DisplayValue
                  label="Created at"
                  type="datetime"
                  value={defaultValues.created}
                />
                <DisplayValue
                  label="Modified at"
                  type="datetime"
                  value={defaultValues.modified}
                />
              </>
            )}
          </LayoutGap>
          <LayoutGap direction="column" className="w-2/3">
            {rules.fields.length === 0 && (
              <EmptyState
                title="No rules added"
                description="Add rules to segment"
                className="my-10"
              />
            )}
            {rules.fields.length > 0 &&
              rules.fields.map((ruleField, index) => (
                <LayoutGap key={ruleField.id} className="items-center">
                  <LayoutGap direction="column" className="flex-grow">
                    <LayoutGap
                      size="small"
                      className="grid flex-grow grid-cols-6"
                    >
                      <Form.Select
                        label="Rule Type"
                        name={`rules.${index}.ruleType`}
                        className="col-span-2"
                        options={ruleTypeOptions(isMutable || index > 0)}
                        onValueChange={(value) => changeRuleType(index, value)}
                        disabled={isActive}
                      />
                      {ruleField.ruleType === 'purchases' && (
                        <>
                          <Form.Select
                            name={`rules.${index}.operator`}
                            label="Operator"
                            options={operatorOptions}
                            disabled={isActive}
                          />
                          <Form.Input
                            className="col-span-3"
                            name={`rules.${index}.transactionCount`}
                            label="Transaction Count"
                            placeholder="Count"
                            type="number"
                            step={1}
                            min={0}
                            disabled={isActive}
                          />
                        </>
                      )}
                      {ruleField.ruleType === 'user' && (
                        <>
                          <Form.Select
                            name={`rules.${index}.operator`}
                            label="Operator"
                            options={listOperatorOptions}
                            defaultValue="in"
                            disabled={isActive}
                          />
                          <Form.Input
                            className="col-span-3"
                            name={`rules.${index}.customSegmentId`}
                            label="Custom Segment ID"
                            placeholder="Enter custom segment ID"
                            disabled={isActive}
                            description="The custom_segment_id from DataBricks."
                          />
                        </>
                      )}
                    </LayoutGap>
                    {ruleField.ruleType === 'purchases' && (
                      <LayoutGap size="small" className="grid grid-cols-2">
                        <Form.DateRange
                          label="Date Range"
                          name={`rules.${index}.dateRange`}
                          disabled={isActive}
                          description={
                            <>
                              Date range is static. It will not adjust over
                              time.
                            </>
                          }
                        />
                        <Form.Input
                          name={`rules.${index}.kardMerchantId`}
                          label="Merchant ID"
                          placeholder="Enter merchant ID"
                          type="text"
                          className="w-full"
                          disabled={isActive}
                        />
                      </LayoutGap>
                    )}
                    <Separator />
                  </LayoutGap>
                  {!form.formState.disabled && !isActive && (
                    <Button
                      type="button"
                      variant="tertiary"
                      icon={<CloseIcon />}
                      onClick={() => rules.remove(index)}
                    />
                  )}
                </LayoutGap>
              ))}
            <LayoutGap className="items-center">
              <Button
                type="button"
                variant="secondary"
                onClick={() => rules.append(transactionRuleSchema.cast({}))}
                disabled={form.formState.disabled || isActive}
              >
                Add Rule
              </Button>
              {rulesArrayError && (
                <p className="text-red-500">{rulesArrayError}</p>
              )}
            </LayoutGap>
            <div className="sticky -bottom-[1px] flex justify-end bg-white py-4">
              <LayoutGap className="items-center">
                {submitError && (
                  <p className="text-red-500">{submitError.message}</p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={isSubmitDisabled}
                >
                  {isEditing ? 'Save' : 'Create'} segment
                </Button>
              </LayoutGap>
            </div>
          </LayoutGap>
        </LayoutGap>
      </form>
    </Form.Provider>
  );
};

export default SegmentDetailsForm;
